import { EditorView, keymap, placeholder as cmPlaceholder } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { markdown } from "@codemirror/lang-markdown";
import { GFM } from "@lezer/markdown";
import { history, historyKeymap, defaultKeymap } from "@codemirror/commands";
import { autocompletion } from "@codemirror/autocomplete";
import type { CompletionSource } from "@codemirror/autocomplete";
import { studioTheme, studioHighlighting } from "./theme";
import { markdownKeymap } from "./commands";
import { listEditingKeymap } from "./list-editing";
import { livePreviewPlugin } from "./live-preview/plugin";
import { updateToolbarState } from "./toolbar";
import { wikilinkExtensions as wikilinkExts, wikilinkCompletions } from "./wikilinks";
import {
  contextRefExtensions as contextRefExts,
  contextRefCompletions,
  contextRefCompletionsWithWorkflow,
} from "./context-references";
import { variableCompletions, insertVariable, type WorkflowVariableDef } from "./variables";

export interface MarkdownEditorConfig {
  content: string;
  placeholder: string;
  minHeight: string;
  flexGrow: boolean;
  readOnly?: boolean;
  enableWikilinks?: boolean;
  enableContextReferences?: boolean;
  workflowId?: string;
  enableVariables?: boolean;
  workflowVariables?: WorkflowVariableDef[];
  toolbar?: HTMLElement | null;
  onChange: (content: string) => void;
}

export function initMarkdownEditor(
  container: HTMLElement,
  config: MarkdownEditorConfig,
): EditorView {
  const extensions = [
    markdown({ extensions: GFM }),
    history(),
    studioTheme,
    studioHighlighting,
    keymap.of([...markdownKeymap, ...listEditingKeymap, ...historyKeymap, ...defaultKeymap]),
    livePreviewPlugin,
    cmPlaceholder(config.placeholder),
    EditorView.lineWrapping,
    EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        config.onChange(update.state.doc.toString());
      }
      // Update toolbar active state on cursor/selection/doc changes
      if (config.toolbar && (update.docChanged || update.selectionSet)) {
        updateToolbarState(config.toolbar, update.state);
      }
    }),
  ];

  // Build a single autocompletion instance with all enabled completion sources
  const completionSources: CompletionSource[] = [];

  if (config.enableWikilinks) {
    extensions.push(...wikilinkExts);
    completionSources.push(wikilinkCompletions);
  }

  if (config.enableContextReferences) {
    extensions.push(...contextRefExts);
    if (config.workflowId) {
      completionSources.push(contextRefCompletionsWithWorkflow(config.workflowId));
    } else {
      completionSources.push(contextRefCompletions);
    }
  }

  if (config.enableVariables && config.workflowVariables) {
    completionSources.push(variableCompletions(config.workflowVariables));
  }

  if (completionSources.length > 0) {
    extensions.push(
      autocompletion({
        override: completionSources,
        activateOnTyping: true,
      }),
    );
  }

  if (config.readOnly) {
    extensions.push(EditorState.readOnly.of(true), EditorView.editable.of(false));
  }

  const state = EditorState.create({
    doc: config.content,
    extensions,
  });

  const view = new EditorView({ state, parent: container });

  // Apply min-height from config — set on both container and cm-editor
  // so clicking anywhere in the area focuses the editor
  if (config.minHeight && config.minHeight !== "0") {
    container.style.minHeight = config.minHeight;
    view.dom.style.minHeight = "100%";
  }

  // Apply flex-grow for fill-container behavior (wiki editor).
  // CM6 already sets display:flex/column on .cm-editor and height:100%
  // on .cm-scroller. We only need to make the container a flex grower
  // and propagate height. Avoid setting flex:1 on .cm-scroller as it
  // changes the scroller's flex sizing and breaks text-indent on lines.
  if (config.flexGrow) {
    container.style.flex = "1";
    container.style.minHeight = "0";
    view.dom.style.height = "100%";
  }

  return view;
}

export { setupToolbar, updateToolbarState } from "./toolbar";
export {
  wikilinkExtensions,
  wikilinkClickHandler,
  wikilinkAutocomplete,
  insertWikilink,
  buildWikilinkDecorations,
} from "./wikilinks";
export {
  contextRefExtensions,
  contextRefAutocomplete,
  insertContextRef,
  buildContextRefDecorations,
} from "./context-references";
export {
  buildVariableDecorations,
  variableCompletions,
  insertVariable,
  type WorkflowVariableDef,
} from "./variables";
